import type { Change } from '@w/crdt'
import { liveScope, sameScope, scopeBlocks } from './scope'
import type { EditorIntent } from './intents'
import { restoredSelection, type CoreSelection, type CoreState } from './state'

export interface HistoryEntry {
  historyToken: string
  group?: string
  scopeBefore?: CoreState['scope']
  scopeAfter?: CoreState['scope']
  selectionBefore: CoreSelection | null
  selectionAfter: CoreSelection | null
  formatBefore?: number
  formatAfter?: number
  emptyFormatsBefore?: CoreState['emptyParagraphFormats']
  emptyFormatsAfter?: CoreState['emptyParagraphFormats']
  change?: EditorIntent['type']
  time?: number
}

export interface CoreHistory {
  undo: HistoryEntry[]
  redo: HistoryEntry[]
}

export interface HistoryTiming {
  time?: number
  delay?: number
  /** Caller-owned history group (`view:` prefix) replacing the computed typing group. */
  group?: string
}

const OVERRIDE = 'view:'

export function historyGroup(previous: CoreState, intent: EditorIntent, timing: HistoryTiming): string | undefined {
  if (timing.group !== undefined) {
    if (!timing.group.startsWith(OVERRIDE)) throw new Error(`History group override must start with "${OVERRIDE}": ${timing.group}`)
    return timing.group
  }
  if (!['insertText', 'deleteContentBackward', 'deleteContentForward'].includes(intent.type)) return undefined
  const fresh = () => `${intent.type}:${previous.doc.historyJSON().undo.length}:${previous.doc.nextCounter}`
  // An edit from a non-collapsed selection is its own step and never absorbs later typing (Lexical history classifies
  // it OTHER, lexical-history/src/index.ts:130-137); a replacing insertion keeps all its graphemes in that one step.
  if (previous.selection && !collapsed(previous.selection)) return intent.type === 'insertText' ? fresh() : undefined
  const last = previous.history?.undo.at(-1)
  if (last?.change === intent.type && !last.group?.startsWith(OVERRIDE) &&
    (last.selectionBefore === null || collapsed(last.selectionBefore)) &&
    sameSelection(last.selectionAfter, previous.selection) &&
    last.formatAfter === previous.pendingFormat &&
    (timing.time === undefined || last.time === undefined || timing.time - last.time < (timing.delay ?? 300))) {
    return last.group
  }
  return fresh()
}

export function recordHistory(previous: CoreState, state: CoreState, change: Change, intent: EditorIntent,
  timing: HistoryTiming = {}, group?: string, transform = false): void {
  const history = state.history = { undo: [...previous.history?.undo ?? []], redo: [...previous.history?.redo ?? []] }
  const last = history.undo.at(-1)
  const after = { scopeAfter: state.scope, selectionAfter: structuredClone(state.selection), formatAfter: state.pendingFormat,
    emptyFormatsAfter: state.emptyParagraphFormats, time: timing.time }
  if (!change.historyToken) {
    if (last) history.undo[history.undo.length - 1] = freezeEntry({ ...last, ...after, change: undefined })
    return
  }
  const entry = last?.historyToken === change.historyToken ? last : {
    historyToken: change.historyToken,
    group,
    scopeBefore: previous.scope,
    selectionBefore: structuredClone(previous.selection),
    formatBefore: previous.pendingFormat,
    emptyFormatsBefore: previous.emptyParagraphFormats,
  }
  const next = freezeEntry({ ...entry, ...after, change: transform ? undefined : intent.type })
  if (entry === last) history.undo[history.undo.length - 1] = next
  else {
    // An entry owns its container/selection pair: a selection in another scope never overwrites its after-state.
    if (last && sameScope(last.scopeAfter, previous.scope) && (!sameSelection(last.selectionAfter, previous.selection) ||
      last.formatAfter !== previous.pendingFormat || last.emptyFormatsAfter !== previous.emptyParagraphFormats)) history.undo[history.undo.length - 1] = freezeEntry({ ...last,
      selectionAfter: structuredClone(previous.selection), formatAfter: previous.pendingFormat,
      emptyFormatsAfter: previous.emptyParagraphFormats })
    history.undo.push(next)
  }
  history.redo = []
}

export function navigateHistory(previous: CoreState, redo: boolean): { state: CoreState; change: Change | null } {
  const history = { undo: [...previous.history?.undo ?? []], redo: [...previous.history?.redo ?? []] }
  const source = redo ? history.redo : history.undo
  const target = redo ? history.undo : history.redo
  const entry = source.pop()
  if (!entry) return { state: previous, change: null }
  const change = redo ? previous.doc.redo(entry.historyToken) : previous.doc.undo(entry.historyToken)
  if (!change) return { state: previous, change: null }
  target.push(freezeEntry({ ...entry, change: undefined }))
  const saved = redo ? entry.selectionAfter : entry.selectionBefore
  const savedScope = redo ? entry.scopeAfter : entry.scopeBefore
  const scope = liveScope(previous.doc, savedScope)
  const blocks = scopeBlocks(previous.doc, scope)
  const state: CoreState = { ...previous, scope, history, blocks,
    selection: savedScope && !scope ? null : restoredSelection(structuredClone(saved), previous.blocks, blocks),
    pendingFormat: redo ? entry.formatAfter : entry.formatBefore,
    emptyParagraphFormats: redo ? entry.emptyFormatsAfter : entry.emptyFormatsBefore,
  }
  return { state, change }
}

function collapsed(selection: CoreSelection | null): boolean {
  return !!selection && selection.anchor.blockId === selection.focus.blockId &&
    selection.anchor.offset === selection.focus.offset
}

function sameSelection(left: CoreSelection | null, right: CoreSelection | null): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}


function freezeEntry<T>(value: T): T {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const child of Object.values(value)) freezeEntry(child)
    Object.freeze(value)
  }
  return value
}
